import { LocalStorageService } from './LocalStorageService';
import {
    CookieConsentPreference,
    CookieConsentStatus,
    LocalStorageKeys,
} from '../types/localStorage';

export const COOKIE_CONSENT_PROMPT_EVENT = 'carton:cookie-consent-prompt';

const COOKIE_CONSENT_VERSION = 1;
const POSTHOG_SCRIPT_ID = 'posthog-script';
const GOOGLE_ADSENSE_SCRIPT_ID = 'google-adsense-script';
const CONTENTSQUARE_SCRIPT_ID = 'contentsquare-script';
const POSTHOG_METHODS = [
    'init',
    'capture',
    'register',
    'register_once',
    'register_for_session',
    'unregister',
    'unregister_for_session',
    'getFeatureFlag',
    'getFeatureFlagPayload',
    'isFeatureEnabled',
    'reloadFeatureFlags',
    'updateEarlyAccessFeatureEnrollment',
    'getEarlyAccessFeatures',
    'on',
    'onFeatureFlags',
    'onSessionId',
    'getSurveys',
    'getActiveMatchingSurveys',
    'renderSurvey',
    'canRenderSurvey',
    'identify',
    'setPersonProperties',
    'group',
    'resetGroups',
    'setPersonPropertiesForFlags',
    'resetPersonPropertiesForFlags',
    'setGroupPropertiesForFlags',
    'resetGroupPropertiesForFlags',
    'reset',
    'get_distinct_id',
    'getGroups',
    'get_session_id',
    'get_session_replay_url',
    'alias',
    'set_config',
    'startSessionRecording',
    'stopSessionRecording',
    'sessionRecordingStarted',
    'captureException',
    'loadToolbar',
    'get_property',
    'getSessionProperty',
    'createPersonProfile',
    'opt_in_capturing',
    'opt_out_capturing',
    'has_opted_in_capturing',
    'has_opted_out_capturing',
    'clear_opt_in_out_capturing',
    'debug',
    'getPageViewId',
];

type PostHogConfig = {
    api_host: string;
    person_profiles: 'identified_only';
    autocapture: boolean;
    capture_pageview: boolean;
    capture_pageleave: boolean;
    capture_exceptions: boolean;
};

interface PostHogQueue extends Array<unknown> {
    [key: string]: unknown;
    __SV?: number;
    _i?: unknown[];
    init?: (apiKey: string, config: PostHogConfig, name?: string) => void;
    people?: PostHogQueue;
    reset?: () => void;
    opt_out_capturing?: () => void;
    toString: (stub?: boolean) => string;
}

interface AdsByGoogleQueue extends Array<unknown> {
    pauseAdRequests?: number;
}

declare global {
    interface Window {
        adsbygoogle?: AdsByGoogleQueue;
        posthog?: PostHogQueue;
    }
}

const isBrowser = () =>
    typeof window !== 'undefined' && typeof document !== 'undefined';

const createPostHogQueue = (): PostHogQueue => [] as unknown as PostHogQueue;

const enqueuePostHogMethod = (target: PostHogQueue, method: string) => {
    const segments = method.split('.');
    let receiver = target;
    let methodName = method;

    if (segments.length === 2) {
        const [namespace, nestedMethodName] = segments;
        const existingReceiver = target[namespace];
        receiver = Array.isArray(existingReceiver)
            ? (existingReceiver as PostHogQueue)
            : createPostHogQueue();
        target[namespace] = receiver;
        methodName = nestedMethodName;
    }

    receiver[methodName] = (...args: unknown[]) => {
        receiver.push([methodName, ...args]);
    };
};

export class CookieConsentService {
    static currentConsent(): CookieConsentPreference | null {
        if (!isBrowser()) {
            return null;
        }

        const preference = LocalStorageService.getItem(
            LocalStorageKeys.COOKIE_CONSENT,
        );

        if (!preference || preference.version !== COOKIE_CONSENT_VERSION) {
            return null;
        }

        return preference;
    }

    static shouldRequestConsent(): boolean {
        return this.currentConsent() === null;
    }

    static applyStoredConsent(): void {
        if (this.currentConsent()?.status === 'accepted') {
            this.loadOptionalScripts();
        }
    }

    static acceptAll(): void {
        this.storeConsent('accepted');
        this.loadOptionalScripts();
    }

    static rejectAll(): void {
        const shouldReload = this.hasOptionalScriptsLoaded();

        this.storeConsent('rejected');
        this.disableOptionalTools();

        if (shouldReload) {
            window.location.reload();
        }
    }

    static requestReview(): void {
        if (!isBrowser()) {
            return;
        }

        window.dispatchEvent(new Event(COOKIE_CONSENT_PROMPT_EVENT));
    }

    private static storeConsent(status: CookieConsentStatus): void {
        LocalStorageService.setItem(LocalStorageKeys.COOKIE_CONSENT, {
            status,
            version: COOKIE_CONSENT_VERSION,
            decidedAt: new Date().toISOString(),
        });
    }

    private static loadOptionalScripts(): void {
        if (!isBrowser()) {
            return;
        }

        this.loadPostHog();
        this.loadGoogleAdsense();
        this.loadContentsquare();
    }

    private static loadPostHog(): void {
        const token = this.metaContent('carton-posthog-project-token');
        const apiHost = this.metaContent('carton-posthog-host');

        if (!token || !apiHost || window.posthog?.__SV) {
            return;
        }

        const posthog = window.posthog || createPostHogQueue();
        window.posthog = posthog;
        posthog._i = [];
        posthog.init = (apiKey, config, name) => {
            if (!document.getElementById(POSTHOG_SCRIPT_ID)) {
                const script = document.createElement('script');
                script.id = POSTHOG_SCRIPT_ID;
                script.type = 'text/javascript';
                script.crossOrigin = 'anonymous';
                script.async = true;
                script.src = `${config.api_host.replace(
                    '.i.posthog.com',
                    '-assets.i.posthog.com',
                )}/static/array.js`;
                document.head.appendChild(script);
            }

            const queue = name
                ? this.postHogNamedQueue(posthog, name)
                : posthog;
            const peopleQueue = queue.people || createPostHogQueue();
            queue.people = peopleQueue;
            queue.toString = (stub?: boolean) => {
                const label = name ? `posthog.${name}` : 'posthog';

                return stub ? label : `${label} (stub)`;
            };
            peopleQueue.toString = () =>
                `${queue.toString(true)}.people (stub)`;

            POSTHOG_METHODS.forEach((method) =>
                enqueuePostHogMethod(queue, method),
            );
            posthog._i?.push([apiKey, config, name]);
        };
        posthog.__SV = 1;
        posthog.init(token, {
            api_host: apiHost,
            person_profiles: 'identified_only',
            autocapture: false,
            capture_pageview: false,
            capture_pageleave: false,
            capture_exceptions: true,
        });
    }

    private static postHogNamedQueue(
        rootQueue: PostHogQueue,
        name: string,
    ): PostHogQueue {
        const existingQueue = rootQueue[name];

        if (Array.isArray(existingQueue)) {
            return existingQueue as PostHogQueue;
        }

        const queue = createPostHogQueue();
        rootQueue[name] = queue;

        return queue;
    }

    private static loadGoogleAdsense(): void {
        const client = this.metaContent('carton-google-adsense-client');

        if (!client || document.getElementById(GOOGLE_ADSENSE_SCRIPT_ID)) {
            return;
        }

        const script = document.createElement('script');
        script.id = GOOGLE_ADSENSE_SCRIPT_ID;
        script.async = true;
        script.crossOrigin = 'anonymous';
        script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(
            client,
        )}`;
        document.head.appendChild(script);
    }

    private static loadContentsquare(): void {
        const scriptSrc = this.metaContent('carton-contentsquare-src');

        if (!scriptSrc || document.getElementById(CONTENTSQUARE_SCRIPT_ID)) {
            return;
        }

        const script = document.createElement('script');
        script.id = CONTENTSQUARE_SCRIPT_ID;
        script.src = scriptSrc;
        document.head.appendChild(script);
    }

    private static disableOptionalTools(): void {
        window.posthog?.opt_out_capturing?.();
        window.posthog?.reset?.();

        if (window.adsbygoogle) {
            window.adsbygoogle.pauseAdRequests = 1;
        }
    }

    private static hasOptionalScriptsLoaded(): boolean {
        return Boolean(
            document.getElementById(POSTHOG_SCRIPT_ID) ||
                document.getElementById(GOOGLE_ADSENSE_SCRIPT_ID) ||
                document.getElementById(CONTENTSQUARE_SCRIPT_ID),
        );
    }

    private static metaContent(name: string): string {
        return (
            document
                .querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
                ?.content.trim() ?? ''
        );
    }
}
